import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import { AssignmentAssociate, ProcessPath } from '../../assignments/models';
import {
  collectRosterEntries,
  deriveAssignment,
  groupAssociatesByLetter,
  LetterGroup,
  paginateGroupsIntoColumns,
} from '../utils';
import AssociateRow from './AssociateRow';

export interface LaborBoardProps {
  associates: AssignmentAssociate[];
  processPaths: ProcessPath[];
  /**
   * Interval (ms) between automatic page advances. Defaults to
   * {@link AUTO_ROTATE_INTERVAL_MS}. A value <= 0 disables auto-rotation
   * (useful for tests).
   */
  autoRotateIntervalMs?: number;
}

/** Default dwell (ms) on each page before auto-advancing, per the mock (8s). */
export const AUTO_ROTATE_INTERVAL_MS = 8 * 1000;
/**
 * Fallback estimate (px) for a single associate card's rendered height, used
 * only until the board measures a real card from the DOM. Real cards are 2–3
 * text lines tall; the board replaces this with the measured value on first
 * layout so packing density matches the actual CSS rather than a guess.
 */
const FALLBACK_CARD_HEIGHT_PX = 48;
/**
 * Fallback estimate (px) for the per-group vertical overhead (inter-group gap
 * + border/box-shadow). The letter cell sits BESIDE the names (flex row), so a
 * group adds no header height of its own — only the gap between groups. The
 * board measures the real gap on first layout and uses that instead.
 */
const FALLBACK_GROUP_OVERHEAD_PX = 10;
/** Number of side-by-side columns the board renders. */
const COLUMNS_PER_PAGE = 2;
/**
 * Fallback column height (px) used before the board has measured its real
 * height (e.g. first render, or in jsdom where layout is zero). Sized to hold
 * roughly a dozen cards so the un-measured board still paginates sensibly.
 */
const FALLBACK_COLUMN_HEIGHT_PX = 12 * FALLBACK_CARD_HEIGHT_PX;
/** Floor on the measured column height so a tiny viewport still shows cards. */
const MIN_COLUMN_HEIGHT_PX = 3 * FALLBACK_CARD_HEIGHT_PX;

/** Assignment lookup keyed by `associateId` (see the memoized map below). */
type AssignmentMap = Map<string | undefined, ReturnType<typeof deriveAssignment>>;

interface LetterGroupColumnProps {
  /** Stable key fragment ("left" / "right") for the column's test id. */
  columnKey: string;
  groups: LetterGroup[];
  assignmentFor: AssignmentMap;
  processPaths: ProcessPath[];
}

/**
 * One of the board's two columns: a vertical stack of letter groups, each a
 * colored letter cell plus its associate cards. Extracted from `LaborBoard`
 * so the render tree stays flat and the column is a stable component identity
 * across re-renders (rather than a per-render inline function).
 */
const LetterGroupColumn: React.FC<LetterGroupColumnProps> = ({
  columnKey,
  groups,
  assignmentFor,
  processPaths,
}) => (
  <div className="staffing-board__col" data-testid={`staffing-board-col-${columnKey}`}>
    {groups.map(group => (
      <div
        key={group.letter}
        className="letter-group"
        data-testid="staffing-letter-group"
        data-letter={group.letter}
      >
        <div className="letter-group__letter" aria-hidden="true">
          <Text type="h300" className="letter-group__letter-text">
            {group.letter}
          </Text>
        </div>
        <div className="letter-group__names">
          {group.associates.map(associate => (
            <AssociateRow
              key={associate.associateId}
              associate={associate}
              assignment={
                assignmentFor.get(associate.associateId) ??
                deriveAssignment(associate, processPaths)
              }
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

/**
 * The Staffing board grid on the Associate View Page.
 *
 * Renders associates as letter-grouped cards laid out in two columns,
 * paginated to fit the viewport, and auto-rotating between pages — matching
 * the staffing-board mock. Responsibilities:
 *
 *   - Group associates alphabetically by login (see
 *     {@link groupAssociatesByLetter}); each group is a colored letter cell
 *     plus that letter's associate cards.
 *   - Measure the column height and pack the groups into per-column-bounded
 *     pages ({@link paginateGroupsIntoColumns}). Packing per column (rather
 *     than sizing a page total and rebalancing) guarantees no column exceeds
 *     its height, so a column with `overflow: hidden` never clips a card that
 *     was counted as "on this page" — every associate surfaces on some page.
 *     Re-measures on resize.
 *   - Auto-advance through the pages on a timer (default 8s) and expose
 *     clickable page dots; clicking a dot jumps to that page and resets the
 *     timer.
 *
 * Meridian `Box`/`Text` provide the layout and typography primitives; the
 * fine-grained visual chrome (letter-cell background, dot indicators, card
 * borders, exact small font sizes) lives in the page-level SCSS, consistent
 * with the rest of this feature. The measurable board region is a plain
 * styled `<div>` so `display: grid` and height measurement behave
 * predictably.
 *
 * The component is strictly read-only: no mutation controls of any kind.
 */
const LaborBoard: React.FC<LaborBoardProps> = ({
  associates,
  processPaths,
  autoRotateIntervalMs = AUTO_ROTATE_INTERVAL_MS,
}) => {
  // Build the full roster (assigned + unassigned) with each associate's path +
  // station. The real API keeps assigned associates ONLY inside processPaths,
  // so we must collect from there, not from the top-level `associates` pool
  // (which holds only unassigned associates). `assignmentFor` maps each
  // associate id to its derived assignment for O(1) lookup at render time.
  const rosterEntries = useMemo(
    () => collectRosterEntries(associates, processPaths),
    [associates, processPaths]
  );

  const assignmentFor = useMemo(() => {
    const map: AssignmentMap = new Map();
    for (const entry of rosterEntries) {
      map.set(entry.associate.associateId, entry.assignment);
    }
    return map;
  }, [rosterEntries]);

  const groups = useMemo(
    () => groupAssociatesByLetter(rosterEntries.map(e => e.associate)),
    [rosterEntries]
  );

  const boardRef = useRef<HTMLDivElement>(null);
  const [columnHeightPx, setColumnHeightPx] = useState<number>(
    FALLBACK_COLUMN_HEIGHT_PX
  );
  // Real rendered card height and per-group overhead, measured from the DOM so
  // packing density matches the actual CSS (and the mock) rather than a
  // hardcoded guess. Seeded with fallbacks for the first render / jsdom.
  const [cardHeightPx, setCardHeightPx] = useState<number>(FALLBACK_CARD_HEIGHT_PX);
  const [groupOverheadPx, setGroupOverheadPx] = useState<number>(
    FALLBACK_GROUP_OVERHEAD_PX
  );
  const [currentPage, setCurrentPage] = useState<number>(0);

  // Measure the usable column height plus a real card's height and the actual
  // inter-group gap. Both columns are full-height children of the grid, so the
  // grid's clientHeight is the height available to each column. Measuring the
  // card and gap from the rendered DOM keeps pagination density accurate across
  // font/zoom/theme changes and avoids over- or under-provisioning. Runs after
  // layout and on every resize.
  useLayoutEffect(() => {
    const measure = () => {
      const el = boardRef.current;
      if (!el) return;
      const measuredHeight = el.clientHeight;
      if (measuredHeight <= 0) return; // jsdom / not yet laid out → keep fallbacks

      const nextColumnHeight = Math.max(MIN_COLUMN_HEIGHT_PX, measuredHeight);
      setColumnHeightPx(prev => (prev === nextColumnHeight ? prev : nextColumnHeight));

      // Tallest rendered card: cards vary (2 vs 3 lines), so pack against the
      // largest so a column never clips its densest card.
      const cards = el.querySelectorAll<HTMLElement>('.staffing-card');
      let maxCard = 0;
      cards.forEach(card => {
        maxCard = Math.max(maxCard, card.offsetHeight);
      });
      if (maxCard > 0) {
        setCardHeightPx(prev => (prev === maxCard ? prev : maxCard));
      }

      // Inter-group gap: read the column's row-gap so the per-group overhead
      // reflects the actual spacing between letter groups (the letter cell is
      // beside the names, so it adds no height of its own).
      const col = el.querySelector<HTMLElement>('.staffing-board__col');
      if (col) {
        const gap = parseFloat(getComputedStyle(col).rowGap || '');
        if (Number.isFinite(gap)) {
          setGroupOverheadPx(prev => (prev === gap ? prev : gap));
        }
      }
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [groups]);

  const pages = useMemo(
    () =>
      paginateGroupsIntoColumns(
        groups,
        columnHeightPx,
        cardHeightPx,
        groupOverheadPx,
        COLUMNS_PER_PAGE
      ),
    [groups, columnHeightPx, cardHeightPx, groupOverheadPx]
  );

  // Keep the current page index in range whenever the page count shrinks
  // (e.g. after a resize increases capacity and collapses pages).
  useEffect(() => {
    setCurrentPage(prev => {
      if (pages.length === 0) return 0;
      return prev >= pages.length ? pages.length - 1 : prev;
    });
  }, [pages.length]);

  // Auto-rotate through pages. Reset whenever the page count, interval, or the
  // manual page selection changes (goToPage bumps currentPage, restarting the
  // dwell so a manual jump gets a full interval before the next advance).
  useEffect(() => {
    if (autoRotateIntervalMs <= 0 || pages.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setCurrentPage(prev => (prev + 1) % pages.length);
    }, autoRotateIntervalMs);
    return () => window.clearInterval(id);
  }, [autoRotateIntervalMs, pages.length, currentPage]);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const safePage = pages.length === 0 ? 0 : Math.min(currentPage, pages.length - 1);
  // Each page is already split into columns by paginateGroupsIntoColumns.
  const pageColumns = pages[safePage] ?? [];
  const leftColumn = pageColumns[0] ?? [];
  const rightColumn = pageColumns[1] ?? [];

  /** Stable dot key: the first letter rendered on a page, else its index. */
  const pageKey = (page: LetterGroup[][], index: number): string =>
    page[0]?.[0]?.letter ?? String(index);

  return (
    <Box className="staffing-board">
      <div
        className="staffing-board__grid"
        data-testid="labor-board-grid"
        ref={boardRef}
      >
        <LetterGroupColumn
          columnKey="left"
          groups={leftColumn}
          assignmentFor={assignmentFor}
          processPaths={processPaths}
        />
        <LetterGroupColumn
          columnKey="right"
          groups={rightColumn}
          assignmentFor={assignmentFor}
          processPaths={processPaths}
        />
      </div>

      {pages.length > 1 && (
        <div className="staffing-board__nav" data-testid="staffing-board-nav">
          <div className="staffing-board__dots">
            {pages.map((page, index) => (
              <button
                // Key by the page's first rendered letter so the key is stable
                // as the page count changes on resize (index keys would remap).
                key={pageKey(page, index)}
                type="button"
                className={
                  index === safePage
                    ? 'staffing-board__dot staffing-board__dot--active'
                    : 'staffing-board__dot'
                }
                aria-label={`Go to page ${index + 1}`}
                aria-current={index === safePage ? 'true' : undefined}
                data-testid="staffing-board-dot"
                onClick={() => goToPage(index)}
              />
            ))}
          </div>
          <Text
            type="b100"
            color="secondary"
            className="staffing-board__page-label"
            data-testid="staffing-board-page-label"
          >
            {`${safePage + 1} / ${pages.length}`}
          </Text>
        </div>
      )}
    </Box>
  );
};

export default LaborBoard;
